type Task = {
  website: string;
  in_progress: number;
  last_updated: string;
  category: string;
  owner: string;
  title: string | null;
  task_structure_shared_uid: string | null;
  to_do: number;
  in_review: number;
  snooze: number;
  priority: string;
  done: number;
};

type MergedTask = Omit<Task, "website" | "owner"> & {
  websites: string[];
  owners: string[];
};

function deduplicateTasks(tasks: Task[]): MergedTask[] {
  const taskMap = new Map<string, MergedTask>();

  for (const task of tasks) {
    // Skip if title or task_structure_shared_uid is null or undefined
    if (!task.title || !task.task_structure_shared_uid) continue;

    const key = `${task.title}|${task.task_structure_shared_uid}`;

    if (!taskMap.has(key)) {
      taskMap.set(key, {
        ...task,
        websites: [task.website],
        owners: [task.owner],
      });
    } else {
      const existing = taskMap.get(key)!;

      existing.to_do += task.to_do;
      existing.in_progress += task.in_progress;
      existing.in_review += task.in_review;
      existing.snooze += task.snooze;
      existing.done += task.done;

      if (!existing.websites.includes(task.website)) {
        existing.websites.push(task.website);
      }
      if (!existing.owners.includes(task.owner)) {
        existing.owners.push(task.owner);
      }
    }
  }

  return Array.from(taskMap.values());
}

function filterAndGroupTasks(tasks: any, allowedUids: any) {
  if (!Array.isArray(tasks)) return [];
  const taskMap = new Map();

  for (const task of tasks) {
    // Skip if no title/uid
    if (!task.title || !task.task_structure_shared_uid) continue;

    // Skip if uid not in allowed list
    if (!allowedUids.includes(task.task_structure_shared_uid)) continue;

    const key = `${task.title}|${task.task_structure_shared_uid}`;

    if (!taskMap.has(key)) {
      taskMap.set(key, {
        ...task,
        websites: [task.website],
        owners: [task.owner],
      });
    } else {
      const existing = taskMap.get(key);

      // Aggregate numeric fields dynamically
      for (const [k, v] of Object.entries(task)) {
        if (typeof v === "number") {
          existing[k] = (existing[k] || 0) + v;
        }
      }

      // Collect unique websites and owners
      if (task.website && !existing.websites.includes(task.website)) {
        existing.websites.push(task.website);
      }
      if (task.owner && !existing.owners.includes(task.owner)) {
        existing.owners.push(task.owner);
      }
    }
  }

  return Array.from(taskMap.values());
}

function mapOwnersToNames(ownerIds: any, allUsers: any) {
  if (!Array.isArray(allUsers)) return [];

  // Convert single ID to array for consistent handling
  const ids = Array.isArray(ownerIds) ? ownerIds : [ownerIds];

  return ids
    .filter((id) => id !== null && id !== undefined && id !== "")
    .map((id) => {
      const user = allUsers.find((u) => String(u.id) === String(id));
      return user ? `${user.first_name} ${user.last_name}` : `Unknown (${id})`;
    });
}

export { deduplicateTasks, filterAndGroupTasks, mapOwnersToNames };
